// Land Intelligence Alerts panel — priority-sorted list with ack flow.
// Brain: [skills/land-intelligence].
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function LandAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAcked, setShowAcked] = useState(false);
  const [filterPriority, setFilterPriority] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (showAcked) params.set('include_acknowledged', 'true');
      if (filterPriority) params.set('priority', filterPriority);
      const resp = await fetch(`${API_BASE}/land/alerts?${params}`, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`alerts: ${resp.status}`);
      const body = await resp.json();
      const sorted = (body.alerts || []).slice().sort((a, b) => {
        const pa = PRIORITY_ORDER[a.priority] ?? 9;
        const pb = PRIORITY_ORDER[b.priority] ?? 9;
        if (pa !== pb) return pa - pb;
        return new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime();
      });
      setAlerts(sorted);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [showAcked, filterPriority]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-zinc-50 p-6 lg:p-10">
      <div className="mx-auto max-w-5xl">
        <header className="mb-6 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Land Intelligence — Alerts
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-900">
              Active Alerts
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
              Stranded asset detections, queue-withdrawal opportunities, equipment
              availability, drought escalations. Generation lands in Batch 7 with
              the scheduled scans; manual creation is available now via the
              backend POST /api/land/alerts endpoint.
            </p>
          </div>
          <Link
            to="/land-intelligence/pipeline"
            className="rounded-xl border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-700 shadow-sm transition-colors hover:bg-zinc-100"
          >
            ← Pipeline
          </Link>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm">
          <label className="flex items-center gap-2 text-sm text-zinc-700">
            <input
              type="checkbox"
              checked={showAcked}
              onChange={(e) => setShowAcked(e.target.checked)}
            />
            Include acknowledged
          </label>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-700"
          >
            <option value="">All priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <button
            type="button"
            onClick={refresh}
            className="ml-auto rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && alerts.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
            Loading alerts…
          </div>
        ) : alerts.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-zinc-300 bg-white p-12 text-center">
            <p className="text-2xl font-bold text-zinc-900">No active alerts</p>
            <p className="mt-2 text-sm text-zinc-600">
              {showAcked
                ? 'No alerts at all yet — they generate from Batch 7 scheduled scans.'
                : 'All clear. Toggle "Include acknowledged" to see history.'}
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {alerts.map((a) => (
              <AlertCard key={a.alert_id} alert={a} onAck={refresh} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AlertCard({ alert, onAck }) {
  const [showAck, setShowAck] = useState(false);
  const [ackBy, setAckBy] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await fetch(`${API_BASE}/land/alerts/${encodeURIComponent(alert.alert_id)}/ack`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          acknowledged_by: ackBy || 'unattributed',
          action_taken: actionTaken || null,
        }),
      });
      setShowAck(false);
      onAck();
    } finally {
      setBusy(false);
    }
  };

  const tone = alert.priority === 'critical' ? 'border-red-300 bg-red-50' :
               alert.priority === 'high' ? 'border-amber-300 bg-amber-50' :
               alert.priority === 'medium' ? 'border-yellow-200 bg-yellow-50' :
               'border-zinc-200 bg-white';

  return (
    <li className={`rounded-2xl border-2 ${tone} p-4 ${alert.acknowledged ? 'opacity-60' : ''}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-base font-bold text-zinc-900">{alert.title}</h3>
        <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-semibold uppercase tracking-wider">
          {alert.priority} · {alert.alert_type}
        </span>
      </div>
      <p className="mt-2 text-sm text-zinc-700">{alert.description}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
        <span>Detected {alert.detected_at ? new Date(alert.detected_at).toLocaleString() : '—'}</span>
        <span>·</span>
        <span>Source: {alert.data_source}</span>
        {alert.affected_parcels?.length > 0 && (
          <>
            <span>·</span>
            <span>
              Affected:{' '}
              {alert.affected_parcels.slice(0, 3).map((id, i) => (
                <React.Fragment key={id}>
                  {i > 0 && ', '}
                  <Link
                    to={`/land-intelligence/parcel/${encodeURIComponent(id)}`}
                    className="font-mono text-zinc-700 underline hover:text-zinc-900"
                  >
                    {id}
                  </Link>
                </React.Fragment>
              ))}
              {alert.affected_parcels.length > 3 && ` +${alert.affected_parcels.length - 3} more`}
            </span>
          </>
        )}
      </div>

      {alert.acknowledged ? (
        <p className="mt-2 text-xs italic text-zinc-600">
          ✓ ack'd by {alert.acknowledged_by || 'unattributed'}
          {alert.acknowledged_at ? ` on ${new Date(alert.acknowledged_at).toLocaleDateString()}` : ''}
          {alert.action_taken ? ` — "${alert.action_taken}"` : ''}
        </p>
      ) : showAck ? (
        <div className="mt-3 space-y-2 rounded-xl border border-zinc-200 bg-white p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              type="text"
              value={ackBy}
              onChange={(e) => setAckBy(e.target.value)}
              placeholder="Acknowledged by"
              className="rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              placeholder="Action taken (optional)"
              className="rounded-lg border border-zinc-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowAck(false)}
              className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="rounded-lg bg-zinc-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-zinc-700 disabled:opacity-50"
            >
              {busy ? 'Acking…' : 'Confirm'}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowAck(true)}
            className="rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-100"
          >
            Acknowledge
          </button>
        </div>
      )}
    </li>
  );
}
